import {
  type ToolActivityOutcome,
  type ToolApprovalsData,
  toolActivityOutcome,
  toolNameOf,
} from "@esi/shared";
import { Ban, Check, CircleAlert, CircleDashed, Clock, FileText, Globe, X } from "lucide-react";
import type { ReactNode } from "react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatDate } from "../i18n/format.ts";
import { currentLocale } from "../i18n/i18n.ts";
import type { ConversationScope } from "./artefacts/queries.ts";
import { CodeApprovalCard, CodeRunRow } from "./CodeRun.tsx";
import { isRunCodePart } from "./code-run.ts";
import {
  approvalExpired,
  approvalIdOf,
  argumentLines,
  type DecisionFailure,
  type ExternalResult,
  externalResultsOf,
  type RefusalReason,
  refusalOf,
  type ToolPart,
} from "./tool-approvals.ts";
import { fetchedPageOf, fetchUrlOf, isWebFetchPart, urlParts } from "./web-fetch.ts";
import { isWebSearchPart, type WebResult, webResultsOf } from "./web-results.ts";

export interface ToolActivityProps {
  part: ToolPart;
  /** The message's `data-tool-approvals`, rebuilt by the server per render. */
  approvals: ToolApprovalsData | undefined;
  /** Why the viewer's decision was refused, when it was (`404` / `409`). */
  failure?: DecisionFailure;
  /**
   * The viewer may answer a pending request (the conversation's creator, project not frozen):
   * shows the approval card. Absent for anyone else, who sees the waiting row.
   */
  onDecide?: (approvalId: string, approved: boolean) => void;
  /** A reply is streaming: decisions wait for it. */
  busy?: boolean;
  /** A later message followed this one: a request still waiting expired with it. */
  superseded?: boolean;
  /** When the message was written (stored messages); else the row's first render. */
  at?: string | undefined;
  /** Where the conversation lives: a code run's files (agent-turn D20). */
  scope?: ConversationScope | null | undefined;
  /** Built inside the timeline's step: a code run names its files (the answer shows them). */
  inStep?: boolean | undefined;
}

/**
 * One outbound tool call in an answer: the approval card while it waits for the viewer, else an
 * activity row with the tool, the exact arguments and the outcome. External text is plain text
 * only (never Markdown or HTML), and links keep `http(s)` URLs only.
 */
export function ToolActivity(props: ToolActivityProps) {
  const { part, approvals, failure, onDecide, busy, superseded, at, scope } = props;
  const approvalId = approvalIdOf(part);
  let outcome = toolActivityOutcome(part, approvals);
  // The server expires a request once another question follows: so does the live thread.
  if (
    outcome === "pending" &&
    (superseded === true || approvalExpired(approvals, approvalId, Date.now()))
  ) {
    outcome = "expired";
  }
  if (failure) {
    outcome = failure === "expired" ? "expired" : "failed";
  }
  if (
    outcome === "pending" &&
    part.state === "approval-requested" &&
    approvalId &&
    onDecide &&
    !failure
  ) {
    if (isRunCodePart(part)) {
      return (
        <CodeApproval
          approvalId={approvalId}
          part={part}
          files={approvals?.[approvalId]?.files ?? []}
          onDecide={onDecide}
          disabled={busy === true}
        />
      );
    }
    return (
      <ApprovalCard
        part={part}
        approvalId={approvalId}
        reasons={approvals?.[approvalId]?.reasons ?? []}
        onDecide={onDecide}
        disabled={busy === true}
      />
    );
  }
  if (isWebSearchPart(part)) {
    return <WebSearchRow part={part} outcome={outcome} failure={failure} at={at} />;
  }
  if (isWebFetchPart(part)) {
    return <WebFetchRow part={part} outcome={outcome} failure={failure} at={at} />;
  }
  if (isRunCodePart(part)) {
    return (
      <CodeRunRow
        part={part}
        outcome={outcome}
        failure={failure}
        scope={scope ?? null}
        filesIn={approvalId ? approvals?.[approvalId]?.files : undefined}
        filesAsNames={props.inStep === true}
      />
    );
  }
  return <ActivityRow part={part} outcome={outcome} failure={failure} />;
}

/** The code approval card, deciding once (the card then gives way to the row). */
function CodeApproval(props: {
  approvalId: string;
  part: ToolPart;
  files: readonly string[];
  onDecide: (approvalId: string, approved: boolean) => void;
  disabled: boolean;
}) {
  const [deciding, setDeciding] = useState(false);
  const decide = (approved: boolean) => {
    setDeciding(true);
    props.onDecide(props.approvalId, approved);
  };
  return (
    <CodeApprovalCard
      part={props.part}
      files={props.files}
      onAllow={() => decide(true)}
      onDecline={() => decide(false)}
      disabled={props.disabled || deciding}
    />
  );
}

/** A tool's label in the active language; unknown tools get the generic one. */
function useToolLabel(part: ToolPart): string {
  const { t } = useTranslation();
  const name = toolNameOf(part as { type: string; toolName?: unknown });
  return name === "web_search" || name === "web_fetch" || name === "run_code"
    ? t(`tools.label.${name}`)
    : t("tools.label.other");
}

const OUTCOME_STYLE: Record<ToolActivityOutcome, { icon: typeof Check | null; className: string }> =
  {
    running: { icon: null, className: "text-fg-secondary" },
    pending: { icon: CircleDashed, className: "text-fg-secondary" },
    done: { icon: Check, className: "text-fg-secondary" },
    blocked: { icon: Ban, className: "text-danger-fg" },
    declined: { icon: X, className: "text-fg-muted" },
    expired: { icon: Clock, className: "text-fg-muted" },
    failed: { icon: CircleAlert, className: "text-danger-fg" },
  };

function ActivityRow(props: {
  part: ToolPart;
  outcome: ToolActivityOutcome;
  failure: DecisionFailure | undefined;
}) {
  const { part, outcome, failure } = props;
  const { t } = useTranslation();
  const label = useToolLabel(part);
  const refusal = refusalOf(part.output);
  const lines = argumentLines(part.input);
  const results = outcome === "done" ? externalResultsOf(part.output) : [];
  const style = OUTCOME_STYLE[outcome];
  const Icon = style.icon;
  const declinedHere = part.state === "approval-responded" && part.approval?.approved === false;
  const argsLabel =
    outcome === "pending"
      ? t("tools.arguments.proposed")
      : outcome === "blocked" ||
          outcome === "declined" ||
          outcome === "expired" ||
          declinedHere ||
          failure !== undefined ||
          refusal?.status === "refused"
        ? t("tools.arguments.notSent")
        : t("tools.arguments.sent");
  const explanation = failure
    ? t(
        failure === "expired"
          ? "tools.expired"
          : failure === "decided"
            ? "tools.alreadyDecided"
            : failure === "budget"
              ? "tools.budget"
              : failure === "userBudget"
                ? "tools.userBudget"
                : "tools.unavailable",
      )
    : outcome === "declined"
      ? t("tools.declined")
      : outcome === "expired"
        ? t("tools.expired")
        : outcome === "failed"
          ? t(refusal?.status === "refused" ? "tools.refused" : "tools.failed")
          : null;
  return (
    <div className="flex flex-col gap-1.5 text-xs">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <span className="inline-flex items-center gap-1.5 font-medium text-fg-secondary">
          <Globe aria-hidden="true" className="size-3.5 shrink-0" strokeWidth={1.75} />
          {label}
        </span>
        <span aria-hidden="true" className="text-fg-muted">
          ·
        </span>
        <span className={cn("inline-flex items-center gap-1 font-medium", style.className)}>
          {Icon ? (
            <Icon aria-hidden="true" className="size-3 shrink-0" strokeWidth={2} />
          ) : (
            <span
              aria-hidden="true"
              className="size-3 rounded-full border-2 border-line border-t-fg-secondary motion-safe:animate-spin"
            />
          )}
          {t(`tools.outcome.${outcome}`)}
        </span>
      </p>
      {lines.length > 0 ? <Arguments label={argsLabel} lines={lines} /> : null}
      {outcome === "blocked" ? (
        <div className="flex flex-col gap-0.5 text-fg-secondary">
          <p>{t("tools.blockedBecause")}</p>
          {refusal && refusal.reasons.length > 0 ? (
            <ul className="flex list-disc flex-col gap-0.5 pl-4">
              {refusal.reasons.map((reason) => (
                <li key={reason}>{t(`tools.reason.${reason}`)}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {explanation ? <p className="text-fg-secondary">{explanation}</p> : null}
      {results.length > 0 ? <Results results={results} /> : null}
    </div>
  );
}

/** The exact arguments, as plain text lines under a quiet label. */
function Arguments(props: { label: string; lines: readonly string[] }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-fg-muted">{props.label}</span>
      <div className="border-l-2 border-line-strong pl-2.5 text-[13px] leading-normal text-fg">
        {props.lines.map((line, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: argument lines are positional
          <p key={i} className="break-words whitespace-pre-wrap">
            {line}
          </p>
        ))}
      </div>
    </div>
  );
}

/** External results: plain text titles and snippets, `http(s)` links only. */
function Results(props: { results: readonly ExternalResult[] }) {
  const { t } = useTranslation();
  return (
    <details className="group">
      <summary className="cursor-pointer font-medium text-fg-muted hover:text-fg">
        {t("tools.results", { count: props.results.length })}
      </summary>
      <div className="mt-1.5 flex flex-col gap-1.5">
        <p className="text-fg-muted">{t("tools.resultsNote")}</p>
        <ol className="flex flex-col divide-y divide-line border-y border-line">
          {props.results.map((r, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: results never reorder
            <li key={i} className="flex flex-col gap-0.5 py-1.5">
              <ResultTitle result={r} />
              {r.snippet ? (
                <p className="text-[13px] leading-normal break-words text-fg-secondary">
                  {r.snippet}
                </p>
              ) : null}
            </li>
          ))}
        </ol>
      </div>
    </details>
  );
}

function ResultTitle({ result }: { result: ExternalResult }) {
  const { t } = useTranslation();
  const host = result.href ? new URL(result.href).host : null;
  if (!result.href) {
    // No safe link (none, or a scheme other than http/https): the title as plain text.
    return result.title ? (
      <span className="font-medium break-words text-fg">{result.title}</span>
    ) : null;
  }
  return (
    <span className="min-w-0 break-words">
      <a
        href={result.href}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="font-medium text-accent-text underline-offset-4 hover:underline"
      >
        {result.title ?? host}
        <span className="sr-only"> {t("app.newTab")}</span>
      </a>
      {result.title && host ? <span className="text-fg-muted"> · {host}</span> : null}
    </span>
  );
}

/** Asks the viewer to allow or decline the exact arguments before they leave ESI. */
function ApprovalCard(props: {
  part: ToolPart;
  approvalId: string;
  /** Why the gate asked (the approvals part's reason codes). */
  reasons: readonly string[];
  onDecide: (approvalId: string, approved: boolean) => void;
  disabled: boolean;
}) {
  const { part, approvalId, reasons, onDecide, disabled } = props;
  const { t } = useTranslation();
  const label = useToolLabel(part);
  const titleId = useId();
  // Until the part leaves `approval-requested` (the card then gives way to the row).
  const [deciding, setDeciding] = useState(false);
  const decide = (approved: boolean) => {
    setDeciding(true);
    onDecide(approvalId, approved);
  };
  const lines = argumentLines(part.input);
  const web = isWebSearchPart(part);
  const fetchUrl = isWebFetchPart(part) ? fetchUrlOf(part.input) : null;
  // The first reason that explains why a page needs the viewer's approval.
  const why = FETCH_APPROVAL_REASONS.find((r) => reasons.includes(r)) ?? "other";
  return (
    <section
      aria-labelledby={titleId}
      className="flex flex-col gap-2.5 rounded-lg border border-line bg-surface-raised p-3.5 text-sm"
    >
      <div className="flex flex-col gap-0.5">
        <p id={titleId} className="font-semibold text-fg">
          {fetchUrl !== null ? t("tools.fetch.approval.title") : t("tools.approval.title")}
        </p>
        <p className="text-fg-secondary">
          {fetchUrl !== null
            ? t(`tools.fetch.approval.${why}`)
            : web
              ? t("tools.web.approval")
              : t("tools.approval.explained", { tool: label })}
        </p>
      </div>
      {fetchUrl !== null ? (
        <FetchAddress url={fetchUrl} />
      ) : web ? (
        <WebQuery input={part.input} />
      ) : (
        <div className="border-l-2 border-line-strong pl-2.5 text-[13px] leading-normal text-fg">
          {lines.map((line, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: argument lines are positional
            <p key={i} className="break-words whitespace-pre-wrap">
              {line}
            </p>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={disabled || deciding} onClick={() => decide(true)}>
          {t("tools.approval.allow")}
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={disabled || deciding}
          onClick={() => decide(false)}
        >
          {t("tools.approval.decline")}
        </Button>
      </div>
    </section>
  );
}

/** "14:05" today, "3 Mar 2026, 14:05" before: when the search ran. */
function searchedAt(iso: string): string {
  const date = new Date(iso);
  const time = new Intl.DateTimeFormat(currentLocale(), { timeStyle: "short" }).format(date);
  return date.toDateString() === new Date().toDateString() ? time : `${formatDate(iso)}, ${time}`;
}

/**
 * The exact query of a web search, quoted in monospace, then any other argument it carried
 * (recency, number of results) as quiet plain text.
 */
function WebQuery({ input }: { input: unknown }) {
  const query =
    typeof input === "object" &&
    input !== null &&
    typeof (input as { query?: unknown }).query === "string"
      ? (input as { query: string }).query
      : null;
  const rest =
    typeof input === "object" && input !== null
      ? argumentLines(
          Object.fromEntries(
            Object.entries(input as Record<string, unknown>).filter(([k]) => k !== "query"),
          ),
        )
      : argumentLines(input);
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      {query !== null ? (
        <q className="block font-mono text-[13px] leading-normal break-words whitespace-pre-wrap text-fg">
          {query}
        </q>
      ) : null}
      {rest.length > 0 ? (
        <p className="text-xs break-words text-fg-muted">{rest.join(" · ")}</p>
      ) : null}
    </div>
  );
}

/**
 * The activity row of a web search: "Searched the web" with the exact query, when and how many
 * results (their titles and domains on demand, links `http(s)` only), or why nothing was sent.
 */
function WebSearchRow(props: {
  part: ToolPart;
  outcome: ToolActivityOutcome;
  failure: DecisionFailure | undefined;
  at: string | undefined;
}) {
  const { part, failure, at } = props;
  // Declined here, before the server settles the part: already nothing is sent.
  const outcome: ToolActivityOutcome =
    props.outcome === "running" &&
    part.state === "approval-responded" &&
    part.approval?.approved === false
      ? "declined"
      : props.outcome;
  const { t } = useTranslation();
  // A row streamed in this session has no stored time: it is the moment it first showed.
  const [seen] = useState(() => new Date().toISOString());
  const refusal = refusalOf(part.output);
  const results = outcome === "done" ? (webResultsOf(part.output) ?? []) : [];
  const style = OUTCOME_STYLE[outcome];
  const Icon = style.icon;
  const reasons = refusal?.reasons ?? [];
  const blockedBy = reasons.includes("classification")
    ? t("tools.web.blocked.classification")
    : reasons.includes("residency")
      ? t("tools.web.blocked.residency")
      : t("tools.web.blocked.other");
  // Reasons other than the one the sentence already gives.
  const more = reasons.filter((r) => r !== "classification" && r !== "residency");
  const explanation: ReactNode = failure
    ? t(
        failure === "expired"
          ? "tools.web.expired"
          : failure === "decided"
            ? "tools.alreadyDecided"
            : failure === "budget"
              ? "tools.budget"
              : failure === "userBudget"
                ? "tools.userBudget"
                : "tools.unavailable",
      )
    : outcome === "blocked"
      ? blockedBy
      : outcome === "declined"
        ? t("tools.web.declined")
        : outcome === "expired"
          ? t("tools.web.expired")
          : outcome === "failed"
            ? t(refusal?.status === "refused" ? "tools.web.refused" : "tools.web.failed")
            : null;
  const heading =
    outcome === "done"
      ? t("tools.web.searched")
      : outcome === "running"
        ? t("tools.web.searching")
        : t("tools.label.web_search");
  return (
    <div className="flex flex-col gap-1.5 text-xs">
      <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-fg-muted">
        <span className="inline-flex items-center gap-1.5 font-medium text-fg-secondary">
          {outcome === "running" ? (
            <span
              aria-hidden="true"
              className="size-3.5 shrink-0 rounded-full border-2 border-line border-t-fg-secondary motion-safe:animate-spin"
            />
          ) : (
            <Globe aria-hidden="true" className="size-3.5 shrink-0" strokeWidth={1.75} />
          )}
          {heading}
        </span>
        {outcome === "done" ? (
          <>
            <span aria-hidden="true">·</span>
            <time dateTime={at ?? seen} className="tabular-nums">
              {searchedAt(at ?? seen)}
            </time>
            <span aria-hidden="true">·</span>
            <span className="tabular-nums">{t("tools.results", { count: results.length })}</span>
          </>
        ) : outcome !== "running" && Icon ? (
          <>
            <span aria-hidden="true">·</span>
            <span className={cn("inline-flex items-center gap-1 font-medium", style.className)}>
              <Icon aria-hidden="true" className="size-3 shrink-0" strokeWidth={2} />
              {t(`tools.outcome.${outcome}`)}
            </span>
          </>
        ) : null}
      </p>
      <WebQuery input={part.input} />
      {explanation ? <p className="text-fg-secondary">{explanation}</p> : null}
      {outcome === "blocked" && more.length > 0 ? (
        <ul className="flex list-disc flex-col gap-0.5 pl-4 text-fg-secondary">
          {more.map((reason) => (
            <li key={reason}>{t(`tools.reason.${reason}`)}</li>
          ))}
        </ul>
      ) : null}
      {results.length > 0 ? <WebResults results={results} /> : null}
    </div>
  );
}

/** The results of a web search: titles and domains, as plain text and safe links. */
function WebResults({ results }: { results: readonly WebResult[] }) {
  const { t } = useTranslation();
  return (
    <details className="group">
      <summary className="w-fit cursor-pointer font-medium text-fg-muted hover:text-fg">
        {t("tools.web.showResults", { count: results.length })}
      </summary>
      <div className="mt-1.5 flex flex-col gap-1.5">
        <p className="text-fg-muted">{t("tools.resultsNote")}</p>
        <ol className="flex flex-col divide-y divide-line border-y border-line">
          {results.map((r, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: results never reorder
            <li key={i} className="flex min-w-0 items-baseline gap-2 py-1.5">
              {r.source ? (
                <span className="w-6 shrink-0 font-medium text-fg-muted tabular-nums">
                  {r.source}
                </span>
              ) : null}
              <span className="min-w-0 break-words">
                {r.href ? (
                  <a
                    href={r.href}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="font-medium text-accent-text underline-offset-4 hover:underline"
                  >
                    {r.title ?? r.domain}
                    <span className="sr-only"> {t("app.newTab")}</span>
                  </a>
                ) : (
                  <span className="font-medium text-fg">{r.title}</span>
                )}
                {r.domain && r.title ? <span className="text-fg-muted"> · {r.domain}</span> : null}
              </span>
            </li>
          ))}
        </ol>
      </div>
    </details>
  );
}

/** The reasons a page's approval card explains, first applicable first. */
const FETCH_APPROVAL_REASONS = ["url_provenance", "fetch_policy", "classification"] as const;

/**
 * The full address a `web_fetch` asks for, in monospace, wrapping anywhere and never shortened:
 * the viewer approves exactly what they read. The host stands out; the rest stays quiet but
 * legible, since a path or query is where data would hide. Plain text, never a link.
 */
function FetchAddress({ url }: { url: string }) {
  const { t } = useTranslation();
  const { scheme, host, rest } = urlParts(url);
  return (
    <figure
      aria-label={t("tools.fetch.address")}
      className="m-0 rounded-md border border-line bg-surface px-2.5 py-2 font-mono text-[13px] leading-normal [overflow-wrap:anywhere] whitespace-pre-wrap text-fg-secondary"
    >
      <p>
        {scheme}
        <span className="font-semibold text-fg">{host}</span>
        {rest}
      </p>
    </figure>
  );
}

/** The address a row shows: a link once the page was read (`http(s)` only), else plain text. */
function FetchedUrl({ url, href }: { url: string; href: string | null }) {
  const { t } = useTranslation();
  const { scheme, host, rest } = urlParts(url);
  const text = (
    <>
      <span className="text-fg-muted">{scheme}</span>
      <span className="font-semibold text-fg">{host}</span>
      <span className="text-fg-muted">{rest}</span>
    </>
  );
  return (
    <p className="min-w-0 font-mono text-[12.5px] leading-normal [overflow-wrap:anywhere]">
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="underline-offset-4 hover:underline"
        >
          {text}
          <span className="sr-only"> {t("app.newTab")}</span>
        </a>
      ) : (
        text
      )}
    </p>
  );
}

/** The provider's refusal codes, as one explanation of the row. */
function refusedBecause(reasons: readonly RefusalReason[]) {
  if (reasons.includes("domain_denied")) {
    return "tools.fetch.refused.domain_denied" as const;
  }
  if (reasons.includes("redirect_denied")) {
    return "tools.fetch.refused.redirect_denied" as const;
  }
  if (reasons.includes("unsupported_type")) {
    return "tools.fetch.refused.unsupported_type" as const;
  }
  if (
    reasons.includes("host_denied") ||
    reasons.includes("address_denied") ||
    reasons.includes("invalid_url")
  ) {
    return "tools.fetch.refused.not_public" as const;
  }
  return "tools.fetch.refused.other" as const;
}

/**
 * The activity row of a web fetch: "Read the page" with the full address (the host emphasised),
 * when, and the page's title; or why it was not read (blocked, declined, not allowed by the
 * organisation, unsupported content, failed). The address is a link only once the page was read:
 * an address that was refused or declined is never one click away.
 */
function WebFetchRow(props: {
  part: ToolPart;
  outcome: ToolActivityOutcome;
  failure: DecisionFailure | undefined;
  at: string | undefined;
}) {
  const { part, failure, at } = props;
  // Declined here, before the server settles the part: already nothing is sent.
  const outcome: ToolActivityOutcome =
    props.outcome === "running" &&
    part.state === "approval-responded" &&
    part.approval?.approved === false
      ? "declined"
      : props.outcome;
  const { t } = useTranslation();
  const [seen] = useState(() => new Date().toISOString());
  const refusal = refusalOf(part.output);
  const url = fetchUrlOf(part.input);
  const page = outcome === "done" ? fetchedPageOf(part.output) : null;
  const reasons: readonly RefusalReason[] = refusal?.reasons ?? [];
  const refused = refusal?.status === "refused";
  const refusedKey = refused ? refusedBecause(reasons) : null;
  const unsupported = refusedKey === "tools.fetch.refused.unsupported_type";
  const style = refused
    ? { icon: unsupported ? CircleAlert : Ban, className: "text-fg-secondary" }
    : OUTCOME_STYLE[outcome];
  const Icon = style.icon;
  const chip = refused
    ? t(unsupported ? "tools.fetch.unsupported" : "tools.fetch.notAllowed")
    : t(`tools.outcome.${outcome}`);
  const blockedBy = reasons.includes("classification")
    ? t("tools.fetch.blocked.classification")
    : reasons.includes("residency")
      ? t("tools.fetch.blocked.residency")
      : t("tools.fetch.blocked.other");
  const more = reasons.filter((r) => r !== "classification" && r !== "residency");
  const explanation: ReactNode = failure
    ? t(
        failure === "expired"
          ? "tools.fetch.expired"
          : failure === "decided"
            ? "tools.alreadyDecided"
            : failure === "budget"
              ? "tools.budget"
              : "tools.unavailable",
      )
    : outcome === "blocked"
      ? blockedBy
      : outcome === "declined"
        ? t("tools.fetch.declined")
        : outcome === "expired"
          ? t("tools.fetch.expired")
          : refusedKey
            ? t(refusedKey)
            : outcome === "failed"
              ? t("tools.fetch.failed")
              : null;
  const heading =
    outcome === "done"
      ? t("tools.fetch.read")
      : outcome === "running"
        ? t("tools.fetch.reading")
        : t("tools.label.web_fetch");
  return (
    <div className="flex flex-col gap-1.5 text-xs">
      <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-fg-muted">
        <span className="inline-flex items-center gap-1.5 font-medium text-fg-secondary">
          {outcome === "running" ? (
            <span
              aria-hidden="true"
              className="size-3.5 shrink-0 rounded-full border-2 border-line border-t-fg-secondary motion-safe:animate-spin"
            />
          ) : (
            <FileText aria-hidden="true" className="size-3.5 shrink-0" strokeWidth={1.75} />
          )}
          {heading}
        </span>
        {outcome === "done" ? (
          <>
            <span aria-hidden="true">·</span>
            <time dateTime={at ?? seen} className="tabular-nums">
              {searchedAt(at ?? seen)}
            </time>
            {page?.source ? (
              <>
                <span aria-hidden="true">·</span>
                <span className="font-medium tabular-nums">{page.source}</span>
              </>
            ) : null}
          </>
        ) : outcome !== "running" && Icon ? (
          <>
            <span aria-hidden="true">·</span>
            <span className={cn("inline-flex items-center gap-1 font-medium", style.className)}>
              <Icon aria-hidden="true" className="size-3 shrink-0" strokeWidth={2} />
              {chip}
            </span>
          </>
        ) : null}
      </p>
      {page?.title ? (
        <p className="text-[13px] leading-snug font-medium break-words text-fg">{page.title}</p>
      ) : null}
      {url !== null ? <FetchedUrl url={page?.href ?? url} href={page?.href ?? null} /> : null}
      {page?.truncated ? <p className="text-fg-muted">{t("tools.fetch.truncated")}</p> : null}
      {explanation ? <p className="text-fg-secondary">{explanation}</p> : null}
      {outcome === "blocked" && more.length > 0 ? (
        <ul className="flex list-disc flex-col gap-0.5 pl-4 text-fg-secondary">
          {more.map((reason) => (
            <li key={reason}>{t(`tools.reason.${reason}`)}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
